import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import AffiliateDisclosure from '../../../components/feature/AffiliateDisclosure';
import SEOHead from '../../../components/feature/SEOHead';
import { blogPosts } from '../../../mocks/blogs';
import { products } from '../../../mocks/products';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  image: string;
  author: string;
  authorAvatar: string;
  date: string;
  category: string;
  metaTitle: string;
  metaDescription: string;
  readTime: string;
  tags: string[];
  content: Array<{
    type: 'intro' | 'h2' | 'paragraph' | 'product-callout' | 'tip-box' | 'conclusion';
    text?: string;
    heading?: string;
    productId?: number;
    tipTitle?: string;
  }>;
}

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [productsInArticle, setProductsInArticle] = useState<any[]>([]);

  useEffect(() => {
    const foundPost = blogPosts.find((b) => b.slug === slug);
    if (foundPost) {
      setPost(foundPost as BlogPost);

      // Get products mentioned in article
      const productIds = foundPost.content
        .filter((c: any) => c.type === 'product-callout' && c.productId)
        .map((c: any) => c.productId);
      const articleProducts = products.filter((p) => productIds.includes(p.id));
      setProductsInArticle(articleProducts);

      // Get related posts (same category, exclude current)
      const related = blogPosts
        .filter((b) => b.category === foundPost.category && b.id !== foundPost.id)
        .slice(0, 3);
      setRelatedPosts(related as BlogPost[]);
    }

    window.scrollTo(0, 0);
  }, [slug]);

  const buildArticleSchema = (p: BlogPost) => ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: p.title,
    description: p.excerpt,
    image: p.image,
    author: { '@type': 'Person', name: p.author },
    publisher: {
      '@type': 'Organization',
      name: 'MeowCentral',
      logo: {
        '@type': 'ImageObject',
        url: 'https://public.readdy.ai/ai/img_res/37bc7df8-b441-456a-b64e-dfe2e72930b6.png',
      },
    },
    datePublished: p.date,
    dateModified: p.date,
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/${p.slug}`,
    articleSection: p.category,
    keywords: p.tags?.join(', '),
  });

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-teal-600 hover:text-teal-700 font-medium">
            ← Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const renderContent = (item: any, index: number) => {
    switch (item.type) {
      case 'intro':
        return (
          <p key={index} className="text-lg text-gray-700 leading-relaxed mb-8 font-medium">
            {item.text}
          </p>
        );
      
      case 'h2':
        return (
          <h2 key={index} className="text-2xl font-bold text-gray-900 mt-12 mb-4">
            {item.heading}
          </h2>
        );
      
      case 'paragraph':
        return (
          <p key={index} className="text-gray-700 leading-relaxed mb-6">
            {item.text}
          </p>
        );
      
      case 'product-callout':
        const product = products.find((p) => p.id === item.productId);
        if (!product) return null;
        return (
          <div key={index} className="my-10 bg-gradient-to-br from-teal-50 to-amber-50 rounded-xl p-6 border-2 border-teal-200">
            <div className="flex items-center gap-4 mb-3">
              <i className="ri-star-fill text-amber-500 text-xl"></i>
              <span className="text-sm font-bold text-teal-700 uppercase tracking-wide">
                Recommended Product
              </span>
            </div>
            <div className="flex gap-6">
              <img
                src={product.image}
                alt={product.name}
                className="w-32 h-32 object-cover rounded-lg"
              />
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl font-bold text-teal-600">${product.price}</span>
                  <span className="px-3 py-1 bg-white rounded-full text-xs font-semibold text-gray-700">
                    {product.retailer}
                  </span>
                </div>
                <a
                  href={product.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
                >
                  Shop Now <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        );
      
      case 'tip-box':
        return (
          <div key={index} className="my-8 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-6">
            <div className="flex items-start gap-3">
              <i className="ri-lightbulb-fill text-amber-600 text-2xl mt-1"></i>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">{item.tipTitle}</h4>
                <p className="text-gray-700 leading-relaxed">{item.text}</p>
              </div>
            </div>
          </div>
        );
      
      case 'conclusion':
        return (
          <p key={index} className="text-lg text-gray-700 leading-relaxed mt-12 mb-8 font-medium border-t-2 border-gray-200 pt-8">
            {item.text}
          </p>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={post.metaTitle}
        description={post.metaDescription}
        keywords={post.tags?.join(', ')}
        canonical={`/blog/${post.slug}`}
        ogImage={post.image}
        ogType="article"
        schema={buildArticleSchema(post)}
      />
      <Navbar />
      <AffiliateDisclosure />

      {/* Hero Section */}
      <div className="relative h-[500px] w-full">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-4 py-1.5 bg-teal-600 text-white text-sm font-semibold rounded-full">
                {post.category}
              </span>
              <span className="text-white/90 text-sm">{post.readTime}</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-4 leading-tight">
              {post.title}
            </h1>
            <div className="flex items-center gap-4">
              <img
                src={post.authorAvatar}
                alt={post.author}
                className="w-12 h-12 rounded-full border-2 border-white"
              />
              <div>
                <p className="text-white font-semibold">{post.author}</p>
                <p className="text-white/80 text-sm">{post.date}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex gap-12">
          {/* Article Body */}
          <article className="flex-1 max-w-3xl">
            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8 pb-8 border-b border-gray-200">
              {post.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              {post.content.map((item, index) => renderContent(item, index))}
            </div>

            {/* Author Bio */}
            <div className="mt-16 p-8 bg-gradient-to-br from-teal-50 to-amber-50 rounded-2xl border border-teal-200">
              <div className="flex items-start gap-6">
                <img
                  src={post.authorAvatar}
                  alt={post.author}
                  className="w-20 h-20 rounded-full border-4 border-white shadow-lg"
                />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">About {post.author}</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Cat behavior specialist and content creator with over 8 years of experience helping cat parents understand their feline friends better. Passionate about sharing practical tips and product recommendations.
                  </p>
                </div>
              </div>
            </div>
          </article>

          {/* Sticky Sidebar */}
          <aside className="w-80 hidden lg:block">
            <div className="sticky top-24 space-y-8">
              {/* Products in This Article */}
              {productsInArticle.length > 0 && (
                <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-shopping-bag-3-fill text-teal-600"></i>
                    Products in This Article
                  </h3>
                  <div className="space-y-4">
                    {productsInArticle.map((product) => (
                      <div key={product.id} className="group">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-32 object-cover rounded-lg mb-2"
                        />
                        <h4 className="font-semibold text-gray-900 text-sm mb-1 line-clamp-2 group-hover:text-teal-600 transition-colors">
                          {product.name}
                        </h4>
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold text-teal-600">${product.price}</span>
                          <a
                            href={product.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-teal-600 hover:text-teal-700 font-semibold whitespace-nowrap"
                          >
                            View →
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Newsletter Signup */}
              <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl p-6 text-white">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-mail-fill text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Never Miss a Post</h3>
                <p className="text-white/90 text-sm mb-4">
                  Get weekly cat care tips, product reviews, and exclusive deals.
                </p>
                <form className="space-y-3">
                  <input
                    type="email"
                    placeholder="Your email"
                    className="w-full px-4 py-3 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-white"
                  />
                  <button
                    type="submit"
                    className="w-full px-4 py-3 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
                  >
                    Subscribe
                  </button>
                </form>
                <p className="text-white/70 text-xs mt-3">
                  Join 12,400+ cat lovers. Unsubscribe anytime.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-teal-600 text-white text-xs font-semibold rounded-full">
                        {relatedPost.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{relatedPost.date}</span>
                      <span>{relatedPost.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default BlogPostPage;